import React, { useState, useCallback, useEffect } from 'react';
import { Modal } from '../../../../components';
import TagsSelector from '../../../../components/TagsSelector';
import { useNotificationContext } from '../../../../providers';
import { useUpdateSourceTagsMutation, useCreateOrEnsureTagsMutation } from '../../../../api';
import { handleError } from '../../../../lib/helpers';

import './SourcesAddTagsModal.scss';

/**
 * Modal component for adding or removing tags on a single source.
 * Opens when user clicks on the tags column in the Sources table.
 * Allows users to add new tags, select from existing tags, or remove tags
 */
const SourcesAddTagsModal = (props) => {
    const { sourceName, sourceId, open, onClose, onSuccess, initialTags = [], moveGroupId } = props;
    const [tags, setTags] = useState([]);
    const [newTags, setNewTags] = useState([]);
    const { notifySuccess, notifyError } = useNotificationContext();
    const [updateSourceTags, { isLoading }] = useUpdateSourceTagsMutation();
    const [createOrEnsureTags] = useCreateOrEnsureTagsMutation();

    const handleTagsChange = useCallback((allTags, newTagsList = []) => {
        setTags(allTags);
        setNewTags(newTagsList);
    }, []);

    useEffect(() => {
        if (open) {
            setTags(initialTags || []);
            setNewTags([]);
        }
    }, [initialTags, open]);

    const handleConfirm = useCallback(async () => {
        try {
            if (newTags.length) {
                await createOrEnsureTags({
                    orgId: sessionStorage.getItem('userOrg'),
                    tags: newTags,
                }).unwrap();
            }

            await updateSourceTags({
                sourceId,
                tags,
                moveGroupId
            }).unwrap();

            notifySuccess({
                description: `Successfully updated tags for ${sourceName}`,
            });
            setTags([]);
            setNewTags([]);
            onSuccess?.();
            onClose();
        } catch (error) {
            console.error('Error updating tags:', error);
            let failureMsg = 'Failed to update tags.';
            let alternateMsg =
                error.error_code === 'permission_denied' ? failureMsg : `${failureMsg} Please try again.`;
            handleError(error, notifyError, alternateMsg);
        }
    }, [
        tags,
        newTags,
        sourceId,
        sourceName,
        createOrEnsureTags,
        updateSourceTags,
        moveGroupId,
        notifySuccess,
        notifyError,
        onSuccess,
        onClose,
    ]);

    const handleCancel = useCallback(() => {
        setTags([]);
        setNewTags([]);
        onClose();
    }, [onClose]);

    return (
        <Modal
            title={`Add or Remove Tags: ${sourceName}`}
            open={open}
            onOk={handleConfirm}
            onCancel={handleCancel}
            okText="Confirm"
            cancelText="Cancel"
            confirmLoading={isLoading}
            className="add-tags-modal"
            width={500}
        >
            <div className="tags-modal-content">
                <TagsSelector
                    value={tags}
                    onChange={handleTagsChange}
                    placeholder="Enter tag name"
                    disabled={isLoading}
                />
            </div>
        </Modal>
    );
};

export default SourcesAddTagsModal;
